import React from "react";
import {
  CheckCircle,
  Info,
  ShieldCheck,
  Warning,
} from "@phosphor-icons/react";
import {
  APP_PLATFORM_LOGO_MAP,
  APPLE_GUIDE_STEPS_IMAGES_MAPPER,
} from "../../../shared/constants";
import useFancybox from "../../../hooks/useFancyBox";

const RedirectLink = ({ href, text }: { href: string; text: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-600 dark:text-blue-400 hover:underline"
  >
    {text}
  </a>
);

const StepImage = ({
  step,
  className,
}: {
  step: keyof typeof APPLE_GUIDE_STEPS_IMAGES_MAPPER;
  className?: string;
}) => (
  <div className="w-max mt-3">
    <img
      src={APPLE_GUIDE_STEPS_IMAGES_MAPPER[step].link}
      alt="Apple App Store Connect"
      data-fancybox="apple-guide"
      data-src={APPLE_GUIDE_STEPS_IMAGES_MAPPER[step].link}
      className={`max-w-[900px] w-full h-auto border border-gray-200 dark:border-neutral-700 cursor-zoom-in rounded-xl shadow-sm my-2 ${className ?? ""}`}
    />
  </div>
);

const StepCard = ({
  number,
  title,
  children,
}: {
  number: number;
  title: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="border border-gray-100 dark:border-neutral-700 rounded-xl p-5 bg-white dark:bg-neutral-800 shadow-sm space-y-3">
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold flex-shrink-0">
        {number}
      </div>
      <h4 className="font-semibold text-gray-900 dark:text-white text-base">{title}</h4>
    </div>
    <div className="pl-11">{children}</div>
  </div>
);

const Callout = ({
  type,
  title,
  children,
}: {
  type: "warning" | "info" | "success";
  title?: string;
  children: React.ReactNode;
}) => {
  const styles = {
    warning: {
      wrapper: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
      icon: <Warning size={16} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />,
      title: "text-amber-800 dark:text-amber-300",
      body: "text-amber-700 dark:text-amber-400",
    },
    info: {
      wrapper: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
      icon: <Info size={16} weight="fill" className="text-blue-500 flex-shrink-0 mt-0.5" />,
      title: "text-blue-800 dark:text-blue-300",
      body: "text-blue-700 dark:text-blue-400",
    },
    success: {
      wrapper: "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
      icon: <CheckCircle size={16} weight="fill" className="text-green-500 flex-shrink-0 mt-0.5" />,
      title: "text-green-800 dark:text-green-300",
      body: "text-green-700 dark:text-green-400",
    },
  };
  const s = styles[type];
  return (
    <div className={`rounded-lg border p-3 flex gap-2.5 ${s.wrapper}`}>
      {s.icon}
      <div>
        {title && <p className={`text-sm font-semibold mb-0.5 ${s.title}`}>{title}</p>}
        <div className={`text-sm leading-relaxed ${s.body}`}>{children}</div>
      </div>
    </div>
  );
};

const AppleGuide: React.FC = () => {
  const [fancyboxRef] = useFancybox({});

  return (
    <section
      id="apple"
      className="scroll-mt-24 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 p-5 shadow-sm space-y-4"
      ref={fancyboxRef}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="size-10 flex items-center justify-center bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 shadow-sm p-1.5 flex-shrink-0">
          <img
            src={APP_PLATFORM_LOGO_MAP.ios}
            alt="Apple logo"
            className="w-full h-auto object-contain"
          />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
            Apple IAP Integration
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Integrating App Store Connect with AppRevBooster
          </p>
        </div>
      </div>

      {/* Intro */}
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
        This guide walks you through connecting your{" "}
        <span className="font-semibold">App Store Connect</span> account to AppRevBooster using an{" "}
        <span className="font-semibold">App Store Connect API key</span>. This connection allows
        AppRevBooster to manage and synchronize your In-App Purchase (IAP) revenues directly from
        your dashboard.
      </p>

      {/* Prerequisites */}
      <div className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4 shadow-sm">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <CheckCircle size={18} weight="duotone" className="text-indigo-500" />
          Before You Begin (Prerequisites)
        </h3>
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          {[
            <>
              You must have <strong>Admin</strong> or <strong>App Manager</strong> role access in{" "}
              <strong>App Store Connect</strong>.
            </>,
            <>
              You will need access to the <strong>App Store Connect</strong> platform to complete
              these steps.
            </>,
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1 w-4 h-4 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                {i + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Steps */}
      <div className="space-y-3">

        {/* Step 1 */}
        <StepCard number={1} title="Generate an App Store Connect API Key">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            To allow AppRevBooster to access your App Store products, you must create an API key
            in App Store Connect.
          </p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <li>
              Sign in to{" "}
              <RedirectLink href="https://appstoreconnect.apple.com/" text="App Store Connect" />{" "}
              and navigate to:{" "}
              <RedirectLink
                href="https://appstoreconnect.apple.com/access/integrations/api"
                text="Users and Access → Integrations → Team Keys"
              />
              . Click <strong>+</strong> to create a new API key.
              <StepImage step="1" />
            </li>
            <li>
              In the <strong>Generate API Key</strong> window:
              <ul className="mt-2 pl-3 list-none space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  Enter a recognizable name for your key.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-0.5">○</span>
                  Set the access level to{" "}
                  <strong>Developer, Finance</strong> (required for AppRevBooster).
                  <StepImage step="2" />
                </li>
              </ul>
            </li>
            <li>
              After the key is created, click <strong>Download</strong> to save the{" "}
              <strong>.p8</strong> private key file.
              <div className="mt-2">
                <Callout type="warning" title="Important">
                  You can download this key <strong>only once</strong>. Store it safely.
                </Callout>
              </div>
              <StepImage step="3" />
            </li>
          </ol>
        </StepCard>

        {/* Step 2 */}
        <StepCard number={2} title="Add the API Key to AppRevBooster">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            In <strong>AppRevBooster</strong>, go to:{" "}
            <RedirectLink
              href="/integrations"
              text="Integrations → Revenue Sources → Add Account → Apple IAP"
            />
            . Fill in the required fields as described below.
          </p>

          <div className="space-y-4">
            {/* API Key */}
            <div className="border border-gray-100 dark:border-neutral-700 rounded-lg p-3 bg-gray-50 dark:bg-neutral-900">
              <h5 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                API Key (.p8 file)
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Upload the <strong>.p8</strong> API key file you downloaded from App Store Connect.
              </p>
              <StepImage step="4" />
            </div>

            {/* Issuer ID */}
            <div className="border border-gray-100 dark:border-neutral-700 rounded-lg p-3 bg-gray-50 dark:bg-neutral-900">
              <h5 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                Issuer ID
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Copy the <strong>Issuer ID</strong> from:{" "}
                <RedirectLink
                  href="https://appstoreconnect.apple.com/access/integrations/api"
                  text="App Store Connect → Users and Access → Integrations → Team Keys"
                />
                . The Issuer ID is displayed <strong>above</strong> the API Keys table.
              </p>
              <StepImage step="5" />
            </div>

            {/* Key ID */}
            <div className="border border-gray-100 dark:border-neutral-700 rounded-lg p-3 bg-gray-50 dark:bg-neutral-900">
              <h5 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                Key ID
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                In the same{" "}
                <RedirectLink
                  href="https://appstoreconnect.apple.com/access/integrations/api"
                  text="Team Keys"
                />{" "}
                section, locate your newly created key in the list and copy the{" "}
                <strong>Key ID</strong> shown next to it.
              </p>
              <StepImage step="6" />
            </div>

            {/* Vendor ID */}
            <div className="border border-gray-100 dark:border-neutral-700 rounded-lg p-3 bg-gray-50 dark:bg-neutral-900">
              <h5 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                Vendor ID
              </h5>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                To find your Apple Vendor ID:
              </p>
              <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1.5">
                <li>
                  In{" "}
                  <RedirectLink href="https://appstoreconnect.apple.com/" text="App Store Connect" />
                  , return to the main dashboard.
                </li>
                <li>
                  Navigate to{" "}
                  <RedirectLink href="https://appstoreconnect.apple.com/trends" text="Sales and Trends" />
                  .
                </li>
                <li>
                  Your Vendor ID (sometimes referred to as{" "}
                  <strong>Organization ID</strong>) will be displayed on this page. Copy and save it.
                  <StepImage step="7" />
                </li>
              </ol>
            </div>
          </div>
        </StepCard>

        {/* Step 3 */}
        <StepCard number={3} title="Complete Integration in AppRevBooster">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            You now have all the necessary details to finish the Apple IAP integration:
          </p>
          <ul className="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1 mb-3">
            <li>
              The <strong>App Store Connect API Key (.p8)</strong> file (from Step 1).
            </li>
            <li>The <strong>Issuer ID</strong> (from Step 2).</li>
            <li>The <strong>Key ID</strong> (from Step 2).</li>
            <li>The <strong>Vendor ID</strong> (from Step 2).</li>
          </ul>
          <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1.5">
            <li>Log in to your AppRevBooster account.</li>
            <li>Navigate to the integration or Add Account section for Apple IAP.</li>
            <li>Upload the <strong>.p8 API Key file</strong>.</li>
            <li>
              Paste the copied <strong>Issuer ID</strong>, <strong>Key ID</strong>, and{" "}
              <strong>Vendor ID</strong> into their corresponding fields.
            </li>
            <li>Save your changes to complete the integration.</li>
          </ol>
          <StepImage step="8" className="!w-auto h-[500px] mt-4" />
        </StepCard>
      </div>

      {/* Completion banner */}
      <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4 flex gap-3">
        <ShieldCheck size={20} weight="fill" className="text-green-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-green-800 dark:text-green-300">
            Integration Complete!
          </p>
          <p className="mt-1 text-sm text-green-700 dark:text-green-400">
            Your Apple App Store Connect account is now integrated with AppRevBooster. You can
            begin managing and analyzing your in-app purchase and subscription data seamlessly
            from your dashboard.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AppleGuide;
